<template>
  <section class="container">
    <div class="col-md-8 mx-auto">
      <h2>Integrity Policy</h2>
      <p><strong>Last revision: : 2025-02-11</strong></p>
      <p>
        Comstream AB, a Swedish entity with registration number 556945-8911, hereinafter
        “Comstream”, operates in accordance with the policy outlined below. Comstream is responsible
        for handling personal data related to the use of its online services, referred to as
        “Jobshark.” Comstream is committed to safeguarding your personal data and ensuring your
        privacy in compliance with GDPR and other applicable laws.
      </p>

      <h2>1. Gathering of information</h2>
      <p>
        To provide Jobshark’s services, we collect and process the following personal information:
      </p>
      <ul>
        <li>
          <strong>For Candidates:</strong>
          <ul>
            <li>
              Personal details such as name, address, phone number, email, education, work
              experience, and CV details.
            </li>
            <li>
              Additional details such as civil status, interests, and areas of expertise, if
              provided by the candidate.
            </li>
          </ul>
        </li>
        <li>
          <strong>For Employers:</strong>
          <ul>
            <li>
              Business contact information such as name, email, phone number, and other relevant
              identifiers.
            </li>
            <li>
              Information related to job postings, including position descriptions and requirements.
            </li>
          </ul>
        </li>
        <li>
          <strong>For Newsletter subscribers:</strong>
          <ul>
            <li>
              We collect and process business contact details such as name, company, and email
              address for individuals subscribed to our newsletter.
            </li>
          </ul>
        </li>
        <li>
          <strong>General:</strong>
          <ul>
            <li>
              Technical data related to your use of Jobshark, such as system logs, browser
              information, and other usage-related details.
            </li>
          </ul>
        </li>
      </ul>
      <p><strong>Legal Basis for Processing:</strong></p>
      <ul>
        <li>
          Your consent (e.g., for account registrations, newsletter subscriptions and cookies).
        </li>
        <li>
          The necessity to perform a contractual obligation (e.g., to facilitate job applications or
          job advertisements).
        </li>
        <li>
          Legitimate interests (e.g., for sending newsletters to contacts who are likely to have a
          professional interest in our services).
        </li>
      </ul>

      <h2>2. Cookies</h2>
      <p>
        Jobshark is using cookies to improve the user experience. To be able to continuously develop
        our services and products and to be able to customize our information to you, we may also
        gather information about what web browser you are using, your IP address and similar
        information. Please see our
        <router-link target="_blank" :to="{ name: 'cookies' }"
          ><strong>Cookie policy</strong></router-link
        >
        for additional information.
      </p>

      <h2>3. Use of information</h2>
      <p>The data collected is used for the following purposes:</p>
      <ul>
        <li>
          <strong>For Candidates:</strong>
          <ul>
            <li>
              To enable applications for job opportunities via Jobshark. Information shared during
              applications may be reviewed and forwarded to relevant employers.
            </li>
            <li>
              To contact candidates with updates, relevant opportunities, or service-related
              communications.
            </li>
          </ul>
        </li>
        <li>
          <strong>For Employers:</strong>
          <ul>
            <li>
              To facilitate the posting of job advertisements and to connect with suitable
              candidates.
            </li>
            <li>
              Comstream may promote job advertisements or positions through Jobshark and external
              channels, such as social media or other job boards.
            </li>
          </ul>
        </li>
        <li>
          <strong>For Newsletter subscribers: </strong>
          <ul>
            <li>
              Newsletter subscribers will receive periodic emails containing relevant industry
              insights, and other content relevant to their business interests.
            </li>
          </ul>
        </li>
        <li>
          <strong>General:</strong>
          <ul>
            <li>
              We may communicate with you via email, SMS, or other channels to provide updates about
              our services or inform you about activities and events we organize.
            </li>
            <li>
              To send you email containing relevant information, updates, or promotional content.
            </li>
            <li>
              To enhance and personalize your experience by improving the functionality and
              performance of Jobshark’s services.
            </li>
            <li>
              To produce aggregated, anonymized statistics for analytical purposes, helping us
              optimize and refine our offerings.
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Your personal data may be shared with trusted third-party service providers, such as hosting
        providers or analytics platforms, solely for the purpose of delivering our services.
      </p>
      <p>
        If we are required by law or by decision by a government, the information about you can be
        handed over to corresponding authority.
      </p>

      <h2>4. Retention period</h2>
      <p>
        We will retain your personal data only as long as it is necessary for the purposes for which
        it was collected. Depending on the lawful basis for processing, this may:
      </p>
      <ul>
        <li>Be regulated in a contract.</li>
        <li>Be dependent on valid consent.</li>
        <lI
          >For newsletter subscribers, we will retain your contact details as long as you remain
          subscribed. Subscribers who no longer wish to receive newsletters can opt out at any time
          by clicking the unsubscribe link included in each email or by contacting us directly at
          <a href="mailto:compliance@jobshark.eu">compliance@jobshark.eu</a>.</lI
        >
        <li>Be stated in legislation.</li>
        <li>Follow an internal assessment based on a legitimate interest assessment (LIA).</li>
      </ul>
      <p>
        We take reasonable actions to ensure personal data is regularly updated and delete outdated
        or incorrect information.
      </p>

      <h2>5. Your rights</h2>
      <p>
        If you no longer wish for your personal data to be gathered, used, or transferred according
        to this policy, you can withdraw your consent at any time by:
      </p>
      <ul>
        <li>
          <strong>Deleting Your Account:</strong> Access your account settings on the platform and
          follow the instructions to delete your account.
        </li>
        <li>
          <strong>Contacting Us:</strong> Send an email to
          <a href="mailto:compliance@jobshark.eu">compliance@jobshark.eu</a> with your request.
        </li>
      </ul>
      <p>In addition to the above, you may also contact us to request:</p>
      <ul>
        <li>Updates or corrections to your personal data.</li>
        <li>Clarifications regarding the use of your personal data.</li>
      </ul>

      <h2>6. Security</h2>
      <p>
        We prioritize the security of your personal data and implement appropriate technical and
        organizational measures to protect it against unauthorized access, alteration, disclosure,
        or destruction. These measures include, but are not limited to:
      </p>
      <ul>
        <li>
          <strong>Data Encryption:</strong> Personal data is encrypted during transmission and
          storage where applicable, ensuring confidentiality and integrity.
        </li>
        <li>
          <strong>Access Controls:</strong> Access to personal data is restricted to authorized
          personnel only, based on their job responsibilities and subject to strict confidentiality
          obligations.
        </li>
        <li>
          <strong>Regular Security Assessments:</strong> Our IT systems undergo regular security
          audits, vulnerability assessments, and updates to address potential threats and improve
          safeguards.
        </li>
        <li>
          <strong>Incident Response:</strong> We maintain an incident response plan to promptly
          address and mitigate any unauthorized access or data breaches.
        </li>
        <li>
          <strong>Secure Infrastructure:</strong> Our platforms are hosted on secure servers with
          firewalls, intrusion detection systems, and monitoring tools to prevent unauthorized
          access.
        </li>
        <li>
          <strong>User Responsibility:</strong> Users are responsible for maintaining the
          confidentiality of their login credentials and notifying us immediately in the event of
          any suspected unauthorized access to their accounts.
        </li>
      </ul>
      <p>
        While we take every reasonable precaution to protect your data, please note that no system
        can guarantee absolute security. In the event of a data breach, we will notify affected
        individuals and relevant authorities as required by applicable laws.
      </p>

      <h2>7. Changes to the privacy policy</h2>
      <p>
        If there are significant changes to this policy, Comstream will notify you via email or
        through a message on the site.
      </p>

      <h2>8. Applicable laws</h2>
      <p>
        This Contract shall be governed by the laws of Sweden and any dispute shall be settled by a
        general Swedish court of law.
      </p>

      <h2>9. Contacting us</h2>
      <p>
        Please contact us if you have questions about your rights or if you have any other questions
        about how we process your personal information:
        <a href="mailto:compliance@jobshark.eu">compliance@jobshark.eu</a>
      </p>
    </div>
  </section>
</template>
<script>
import titleMixin from '../../mixins/titleMixin';
export default {
  mixins: [titleMixin],
  title: 'Integrity Policy | Jobshark',
};
</script>
<style lang="scss" scoped>
.container {
  padding: 80px 0;
}
</style>
